import { AbstractControl, UntypedFormArray, UntypedFormGroup, ValidationErrors, Validators } from '@angular/forms';

/**
 * Depending on the controls in the form group, a soft delete strategy is inferred and returned
 * @param group
 * @returns
 */
const getSoftDeletionStrategy = (group: UntypedFormGroup) => {
  const { _mode, deleted, _deleted } = group.controls;
  if (_mode != null) {
    return (fg: UntypedFormGroup) => fg.value._mode !== 'delete';
  } else if (deleted != null || _deleted != null) {
    const attr = deleted != null ? 'deleted' : '_deleted';
    return (fg: UntypedFormGroup) => !fg.value[attr];
  }
  return null;
};

/**
 * if entries size is 0 then return ValidationErrors else return null
 * @param entries
 * @returns
 */
const atLeastOneEntryRequired = (entries: any[]) => (entries.length === 0 ? MiqCatalog.EntryRequiredError : null);

/**
 * Common static form validation class
 */
export class MiqCatalog {
  static EntryRequiredError = { entryRequired: true };

  /**
   * Generic required version to support soft delete on form arrays. When a section of type array is marked as required
   * an error should be bound to the control and rendered at message center and section container. Few known strategies
   * for soft delete has been added to the entries count.
   *
   * Supported Strategies
   * - FormArray such tha each FormGroup within contains a _mode control with  'delete' word
   * - FormArray such tha each FormGroup within contains a boolean control named deleted
   * - FormArray such tha each FormGroup within contains a boolean control named _deleted
   *
   * Any other soft delete added to the framework should be added to the strategies inferrer function (`getSoftDeletionStrategy`)
   *
   */
  public static required(control: AbstractControl): ValidationErrors | null {
    if (control instanceof UntypedFormArray) {
      const [firstGroup] = control.controls;
      if (firstGroup instanceof UntypedFormGroup) {
        // infer soft delete strategy
        const bySoftDeleteStrategy = getSoftDeletionStrategy(firstGroup as UntypedFormGroup);
        if (bySoftDeleteStrategy != null) {
          // if strategy found use to filter controls entries
          const entries = control.controls.filter(bySoftDeleteStrategy);
          // return validate soft deleted controls
          MiqCatalog.updateParentValidity(control);
          return atLeastOneEntryRequired(entries);
        }
      }
      // return validate controls
      MiqCatalog.updateParentValidity(control);
      return atLeastOneEntryRequired(control.controls);
    }
    // validate with default required ValidatorFn
    return Validators.required(control);
  }

  // * This fixes MIQ-24689...
  // * Child control validations are working correctly but valid status is not propagating to parent formGroup...
  static updateParentValidity(control: AbstractControl) {
    setTimeout(() => control?.parent?.updateValueAndValidity({ emitEvent: false, onlySelf: true }));
  }
}
